'use client';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog-sticky-footer';
import { Button } from '@/components/ui/button';
import { Star } from 'lucide-react';
import { AnimatedImageGrid } from '@/components/ui/animated-image-grid';
import Image from 'next/image';
import NumberOne from '@/components/Badge/NumberOne';
import CustomerAvatars from '@/components/Marketing/CustomerAvatars';
import { logos, MiniLogoCloud } from './LogoCloud/LogoCloud';
import { Card } from '@components/ui/card';
import { useSession } from '@hooks/index';
import { useRouter } from 'next/navigation';

interface DiscountDialogProps {
  discountCode: string;
  discountPercentage: number;
  onApplyCoupon: (applied: boolean) => void;
  /**
   * Controls the open state of the dialog
   */
  isOpen: boolean;
  /**
   * Callback when dialog open state changes
   */
  onOpenChange: (open: boolean) => void;
}

export function DiscountDialog({
  discountCode,
  discountPercentage,
  onApplyCoupon,
  isOpen,
  onOpenChange
}: DiscountDialogProps) {
  const { isLoggedIn } = useSession();
  const router = useRouter();

  const handleApplyCoupon = () => {
    if (isLoggedIn) {
      onApplyCoupon(true);
      onOpenChange(false);
    } else {
      // Redirect to sign-up page with pricing redirect and coupon code
      const redirectUrl = `/pricing?code=${discountCode}`;
      router.push(`/login?redirect=${encodeURIComponent(redirectUrl)}&mode=sign-up&code=${discountCode}`);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="border-none flex flex-col gap-0 p-0 md:h-auto sm:max-w-screen-lg">
        <div className="flex overflow-hidden h-fit">
          {/* Left side - Animated Image Grid */}
          <div className="hidden md:block w-1/2 h-auto relative overflow-hidden bg-black">
            <AnimatedImageGrid
              columnCount={3}
              className="absolute inset-0 scale-110 flex gap-2 bg-black"
              imageClassName="w-full aspect-[2/3] rounded-md object-cover"
              opacity="opacity-100"
              columnVisibilityClasses={['', '', 'hidden lg:block']}
            />
            {/* Add a gradient overlay for better text readability */}
            <div className="absolute inset-0 bg-gradient-to-r from-black to-black/50 z-10 opacity-50" />
          </div>

          {/* Right side - Content */}
          <div className="w-full md:w-1/2 flex flex-col sm:max-h-[640px] sm:h-screen bg-background p-6">
            <div className="flex-1 flex flex-col justify-center items-start text-left px-4">
              <DialogHeader className="mb-6 text-left">
                <DialogTitle className="text-4xl font-medium text-left">Get {discountPercentage}% off!</DialogTitle>

                <DialogDescription className="text-lg text-foreground text-left">
                  Get your AI generated headshot today. As a promotional offer, get {discountPercentage}% off on your
                  purchase.
                </DialogDescription>
              </DialogHeader>

              <Button onClick={handleApplyCoupon} className="w-full max-w-md mb-8" size="lg" variant="accent">
                Yes, Apply {discountPercentage}% Discount!
              </Button>
              <div className="flex flex-col gap-4">
                <div className="flex gap-2 w-full justify-around">
                  <NumberOne />
                  <div className="flex flex-col items-center justify-center">
                    <CustomerAvatars />
                  </div>
                </div>

                <MiniLogoCloud
                  textClassName="text-xs text-muted-foreground mb-2"
                  logos={logos}
                  maxLogos={4}
                  mobileColumns={4}
                  desktopColumns={4}
                  gap="xs"
                  className="px-10 dark:invert"
                />
                {/* Customer Review */}
                <Card className="bg-card rounded-lg p-4 mb-8 w-full text-left">
                  <div className="flex items-center mb-2 gap-2">
                    <div className="flex items-center gap-2">
                      <Image
                        src="https://www.picstudio.ai/images/marketing/Brandon04.webp"
                        alt="Brandon's profile"
                        width={32}
                        height={32}
                        className="rounded-full object-cover"
                      />
                      <span className="font-medium">Brandon</span>
                    </div>
                    <div className="flex">
                      {Array(5)
                        .fill(0)
                        .map((_, i) => (
                          <Star key={i} className="h-3 w-3 fill-amber-400 text-amber-400" />
                        ))}
                    </div>
                  </div>
                  <p className="text-sm text-left">
                    <span className="font-bold">Absolutely phenomenal AI tool!</span> It exceeded my expectations and
                    transformed my profile picture effortlessly.
                  </p>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
